import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, ShoppingCart } from "lucide-react";
import {
  getCart,
  cartSubtotal,
  updateQty,
  removeFromCart,
  CART_CHANGE_EVENT,
} from "@/lib/cart";
import { LazyImage } from "./LazyImage";

const formatINR = (n) =>
  `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export const CartDrawer = ({ open, onClose, onCheckout }) => {
  const [items, setItems] = useState(() => getCart());

  useEffect(() => {
    const reload = () => setItems(getCart());
    reload();
    window.addEventListener(CART_CHANGE_EVENT, reload);
    window.addEventListener("storage", reload);
    return () => {
      window.removeEventListener(CART_CHANGE_EVENT, reload);
      window.removeEventListener("storage", reload);
    };
  }, []);

  const subtotal = cartSubtotal(items);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex justify-end bg-black/45 backdrop-blur-sm"
          onClick={onClose}
          data-testid="cart-drawer"
        >
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 240, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="relative h-full w-full sm:w-[400px] sm:max-w-[90vw] bg-white shadow-[-20px_0_60px_rgba(0,0,0,0.18)] flex flex-col"
          >
            <header className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-black/5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#022C22] flex items-center justify-center">
                  <ShoppingCart
                    size={16}
                    strokeWidth={1.8}
                    className="text-[#E0B53C]"
                  />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
                    Your bag
                  </p>
                  <p
                    className="font-display text-lg leading-none"
                    data-testid="cart-item-count"
                  >
                    {items.length} {items.length === 1 ? "item" : "items"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="cart-close-btn"
                aria-label="close"
                className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 flex items-center justify-center transition-colors"
              >
                <X size={16} strokeWidth={1.8} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4">
              {items.length === 0 ? (
                <div
                  className="h-full flex flex-col items-center justify-center text-center py-16"
                  data-testid="cart-empty-state"
                >
                  <div className="w-14 h-14 rounded-full bg-[#FFF5D1] flex items-center justify-center mb-4">
                    <ShoppingCart
                      size={22}
                      strokeWidth={1.6}
                      className="text-[#064E3B]"
                    />
                  </div>
                  <p className="font-display text-xl text-[#0B0C10] mb-1">
                    Your bag is empty.
                  </p>
                  <p className="text-sm text-neutral-500">
                    Found something you love? Tap the heart of the bazaar.
                  </p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {items.map((item) => (
                    <li
                      key={item.key}
                      data-testid={`cart-item-${item.key}`}
                      className="flex gap-3 p-3 rounded-2xl bg-neutral-50 border border-black/5"
                    >
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-white shrink-0 relative">
                        <LazyImage
                          src={item.image}
                          alt={item.title}
                          wrapperClassName="w-full h-full"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-display text-sm sm:text-base text-[#0B0C10] line-clamp-2 leading-snug">
                            {item.title}
                          </h4>
                          <button
                            onClick={() => removeFromCart(item.key)}
                            data-testid={`cart-remove-${item.key}`}
                            aria-label="remove"
                            className="p-1 -m-1 text-neutral-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={14} strokeWidth={1.7} />
                          </button>
                        </div>
                        <p
                          className="text-[11px] uppercase tracking-[0.18em] text-[#064E3B] font-semibold mt-0.5 flex items-center gap-1.5"
                          data-testid={`cart-item-size-${item.key}`}
                        >
                          <span>Size · {item.size || "Free Size"}</span>
                          {item.color && (
                            <>
                              <span className="text-neutral-300">·</span>
                              <span
                                className="inline-flex items-center gap-1"
                                data-testid={`cart-item-color-${item.key}`}
                              >
                                <img
                                  src={item.color}
                                  alt={item.colorLabel || "color"}
                                  className="w-3.5 h-3.5 rounded-full object-cover ring-1 ring-black/10"
                                  loading="lazy"
                                />
                                {item.colorLabel || "Variant"}
                              </span>
                            </>
                          )}
                        </p>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <QtyStepper
                            qty={item.qty}
                            onMinus={() => updateQty(item.key, item.qty - 1)}
                            onPlus={() => updateQty(item.key, item.qty + 1)}
                            testIdRoot={item.key}
                          />
                          <p className="font-display text-sm font-medium">
                            {formatINR(item.price * item.qty)}
                          </p>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <footer className="border-t border-black/5 px-5 sm:px-6 py-4 space-y-3 bg-white">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-neutral-600 uppercase tracking-[0.18em]">
                    Subtotal
                  </span>
                  <span
                    className="font-display text-2xl text-[#0B0C10]"
                    data-testid="cart-subtotal"
                  >
                    {formatINR(subtotal)}
                  </span>
                </div>
                <button
                  onClick={() => onCheckout?.(items)}
                  data-testid="cart-checkout-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full py-3.5 text-sm font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all"
                >
                  <ShoppingBag size={16} strokeWidth={1.8} />
                  Checkout · {formatINR(subtotal)}
                </button>
                <p className="text-[11px] text-center text-neutral-500 uppercase tracking-[0.18em]">
                  Cash on Delivery · Free shipping ₹999+
                </p>
              </footer>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const QtyStepper = ({ qty, onMinus, onPlus, testIdRoot }) => (
  <div className="inline-flex items-center bg-white border border-black/10 rounded-full overflow-hidden">
    <button
      onClick={onMinus}
      data-testid={`cart-qty-minus-${testIdRoot}`}
      aria-label="decrease quantity"
      className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100"
    >
      <Minus size={13} strokeWidth={1.8} />
    </button>
    <span
      className="w-8 text-center text-sm font-medium"
      data-testid={`cart-qty-${testIdRoot}`}
    >
      {qty}
    </span>
    <button
      onClick={onPlus}
      data-testid={`cart-qty-plus-${testIdRoot}`}
      aria-label="increase quantity"
      className="w-8 h-8 flex items-center justify-center hover:bg-neutral-100"
    >
      <Plus size={13} strokeWidth={1.8} />
    </button>
  </div>
);
